"use client"

import {
  useEffect,
  useRef,
} from "react"

import type { CurrentTrackData } from "../musicContext"

type Params = {
  isRadio: boolean

  isNativeApp: boolean

  audioRef: React.RefObject<
    HTMLAudioElement | null
  >

  currentTrackData:
    | CurrentTrackData
    | null

  playRadioStream: (
    id: string
  ) => Promise<void>

  stopRadioStream: () => void

  activeRadio:
    | string
    | null

  resumeAudio: () => Promise<boolean>

  previousSong: () => void

  nextSong: () => void

  wantsToPlayRef:
    React.MutableRefObject<boolean>

  setIsMusicLoading: (
    value: boolean
  ) => void

  setIsPlaying: (
    value: boolean
  ) => void
}

export const useMediaSession = ({
  isRadio,
  isNativeApp,
  audioRef,
  currentTrackData,
  playRadioStream,
  stopRadioStream,
  activeRadio,
  resumeAudio,
  previousSong,
  nextSong,
  wantsToPlayRef,
  setIsMusicLoading,
  setIsPlaying,
}: Params) => {
  /* =====================================================
     MEDIA SESSION
  ===================================================== */

  const mediaSessionRef =
    useRef<MediaSession | null>(null)

  /* =====================================================
     METADATA KEY
  ===================================================== */

  const metadataKeyRef =
    useRef<string | null>(null)

  /* =====================================================
     LIVE REFS
  ===================================================== */

  const isRadioRef =
    useRef(isRadio)

  const activeRadioRef =
    useRef(activeRadio)

  const currentTrackDataRef =
    useRef(currentTrackData)

  const playRadioStreamRef =
    useRef(playRadioStream)

  const stopRadioStreamRef =
    useRef(stopRadioStream)

  const resumeAudioRef =
    useRef(resumeAudio)

  const previousSongRef =
    useRef(previousSong)

  const nextSongRef =
    useRef(nextSong)

  const setIsMusicLoadingRef =
    useRef(setIsMusicLoading)

  const setIsPlayingRef =
    useRef(setIsPlaying)

  useEffect(() => {
    isRadioRef.current =
      isRadio
  }, [isRadio])

  useEffect(() => {
    activeRadioRef.current =
      activeRadio
  }, [activeRadio])

  useEffect(() => {
    currentTrackDataRef.current =
      currentTrackData
  }, [currentTrackData])

  useEffect(() => {
    playRadioStreamRef.current =
      playRadioStream
  }, [playRadioStream])

  useEffect(() => {
    stopRadioStreamRef.current =
      stopRadioStream
  }, [stopRadioStream])

  useEffect(() => {
    resumeAudioRef.current =
      resumeAudio
  }, [resumeAudio])

  useEffect(() => {
    previousSongRef.current =
      previousSong
  }, [previousSong])

  useEffect(() => {
    nextSongRef.current =
      nextSong
  }, [nextSong])

  useEffect(() => {
    setIsMusicLoadingRef.current =
      setIsMusicLoading
  }, [setIsMusicLoading])

  useEffect(() => {
    setIsPlayingRef.current =
      setIsPlaying
  }, [setIsPlaying])

  /* =====================================================
     INITIALIZE MEDIA SESSION
  ===================================================== */

  useEffect(() => {
    if (
      typeof navigator ===
        "undefined" ||
      !("mediaSession" in navigator)
    ) {
      return
    }

    mediaSessionRef.current =
      navigator.mediaSession

    try {
      navigator.mediaSession.playbackState =
        "none"
    } catch {}

    return () => {
      try {
        navigator.mediaSession.metadata =
          null

        navigator.mediaSession.playbackState =
          "none"

        navigator.mediaSession.setActionHandler(
          "play",
          null
        )

        navigator.mediaSession.setActionHandler(
          "pause",
          null
        )

        navigator.mediaSession.setActionHandler(
          "previoustrack",
          null
        )

        navigator.mediaSession.setActionHandler(
          "nexttrack",
          null
        )

        navigator.mediaSession.setActionHandler(
          "seekto",
          null
        )

        navigator.mediaSession.setActionHandler(
          "seekbackward",
          null
        )

        navigator.mediaSession.setActionHandler(
          "seekforward",
          null
        )
      } catch {}

      mediaSessionRef.current =
        null
    }
  }, [])

  /* =====================================================
     AUDIO EVENTS
     
     El audio puede cambiar porque hacemos swap
     entre audioRef y nextAudioRef.
  ===================================================== */

  useEffect(() => {
    let audio:
      | HTMLAudioElement
      | null = null

    let cleanup:
      | (() => void)
      | null = null

    const attach =
      () => {
        const nextAudio =
          audioRef.current

        if (
          !nextAudio ||
          nextAudio === audio
        ) {
          return
        }

        cleanup?.()

        audio =
          nextAudio

        /* ===============================================
           PLAY
        =============================================== */

        const handlePlay =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            wantsToPlayRef.current =
              true

            setIsMusicLoadingRef.current(
              false
            )

            setIsPlayingRef.current(
              true
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "playing"
              } catch {}
            }
          }

        /* ===============================================
           PLAYING
        =============================================== */

        const handlePlaying =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            wantsToPlayRef.current =
              true

            setIsMusicLoadingRef.current(
              false
            )

            setIsPlayingRef.current(
              true
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "playing"
              } catch {}
            }
          }

        /* ===============================================
           PAUSE
        =============================================== */

        const handlePause =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            setIsMusicLoadingRef.current(
              false
            )

            /*
             * MUY IMPORTANTE:
             *
             * Si wantsToPlay = true significa que
             * estamos cambiando de canción.
             *
             * NO debemos convertir MediaSession
             * a paused aquí.
             */

            if (
              wantsToPlayRef.current
            ) {
              return
            }

            setIsPlayingRef.current(
              false
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "paused"
              } catch {}
            }
          }

        /* ===============================================
           WAITING
        =============================================== */

        const handleWaiting =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            if (
              nextAudio.paused ||
              nextAudio.ended
            ) {
              return
            }

            setIsMusicLoadingRef.current(
              true
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "playing"
              } catch {}
            }
          }

        /* ===============================================
           CANPLAY
        =============================================== */

        const handleCanPlay =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            if (
              nextAudio.paused ||
              nextAudio.ended
            ) {
              return
            }

            setIsMusicLoadingRef.current(
              false
            )

            setIsPlayingRef.current(
              true
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "playing"
              } catch {}
            }
          }

        /* ===============================================
           ENDED
        =============================================== */

        const handleEnded =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            /*
             * La siguiente canción se encargará
             * de continuar la sesión.
             */

            if (
              wantsToPlayRef.current
            ) {
              return
            }

            setIsPlayingRef.current(
              false
            )

            setIsMusicLoadingRef.current(
              false
            )

            const mediaSession =
              mediaSessionRef.current

            if (mediaSession) {
              try {
                mediaSession.playbackState =
                  "none"
              } catch {}
            }
          }

        /* ===============================================
           ERROR
        =============================================== */

        const handleError =
          () => {
            if (
              audioRef.current !==
              nextAudio
            ) {
              return
            }

            setIsMusicLoadingRef.current(
              false
            )

            setIsPlayingRef.current(
              false
            )

            if (
              !wantsToPlayRef.current
            ) {
              const mediaSession =
                mediaSessionRef.current

              if (mediaSession) {
                try {
                  mediaSession.playbackState =
                    "none"
                } catch {}
              }
            }
          }

        /* ===============================================
           REGISTER
        =============================================== */

        nextAudio.addEventListener(
          "play",
          handlePlay
        )

        nextAudio.addEventListener(
          "playing",
          handlePlaying
        )

        nextAudio.addEventListener(
          "pause",
          handlePause
        )

        nextAudio.addEventListener(
          "waiting",
          handleWaiting
        )

        nextAudio.addEventListener(
          "canplay",
          handleCanPlay
        )

        nextAudio.addEventListener(
          "ended",
          handleEnded
        )

        nextAudio.addEventListener(
          "error",
          handleError
        )

        cleanup =
          () => {
            nextAudio.removeEventListener(
              "play",
              handlePlay
            )

            nextAudio.removeEventListener(
              "playing",
              handlePlaying
            )

            nextAudio.removeEventListener(
              "pause",
              handlePause
            )

            nextAudio.removeEventListener(
              "waiting",
              handleWaiting
            )

            nextAudio.removeEventListener(
              "canplay",
              handleCanPlay
            )

            nextAudio.removeEventListener(
              "ended",
              handleEnded
            )

            nextAudio.removeEventListener(
              "error",
              handleError
            )
          }
      }

    attach()

    const interval =
      window.setInterval(
        attach,
        100
      )

    return () => {
      window.clearInterval(
        interval
      )

      cleanup?.()
    }
  }, [
    audioRef,
  ])

  /* =====================================================
     METADATA
  ===================================================== */

  useEffect(() => {
    const mediaSession =
      mediaSessionRef.current

    if (!mediaSession) {
      return
    }

    const data =
      currentTrackData

    if (!data) {
      return
    }

    const type =
      data.type === "radio"
        ? "radio"
        : "song"

    const title =
      data.trackName ||
      data.track ||
      "Mixtape"

    const artist =
      data.trackArtist ||
      "Mixtape"

    const artwork =
      data.trackImg ||
      ""

    const album =
      type === "radio"
        ? data.radioName ||
          "Mixtape Radio"
        : data.playlistTitle ||
          "Mixtape"

    const key = [
      type,
      title,
      artist,
      artwork,
      album,
    ].join("|")

    if (
      metadataKeyRef.current ===
      key
    ) {
      return
    }

    metadataKeyRef.current =
      key

    try {
      mediaSession.metadata =
        new MediaMetadata({
          title,
          artist,
          album,

          artwork: artwork
            ? [
                {
                  src: artwork,
                  sizes:
                    "512x512",
                  type:
                    "image/jpeg",
                },
              ]
            : [],
        })
    } catch (error) {
      console.warn(
        "[MediaSession] Metadata error:",
        error
      )
    }
  }, [
    currentTrackData?.type,
    currentTrackData?.trackName,
    currentTrackData?.trackArtist,
    currentTrackData?.trackImg,
    currentTrackData?.playlistTitle,
    currentTrackData?.radioName,
  ])

  /* =====================================================
     POSITION STATE
     
     SOLO MÚSICA.
     
     También detectamos el swap de audio.
  ===================================================== */

  useEffect(() => {
    let audio:
      | HTMLAudioElement
      | null = null

    let cleanup:
      | (() => void)
      | null = null

    const attach =
      () => {
        const nextAudio =
          audioRef.current

        if (
          !nextAudio ||
          nextAudio === audio
        ) {
          return
        }

        cleanup?.()

        audio =
          nextAudio

        const updatePosition =
          () => {
            const mediaSession =
              mediaSessionRef.current

            if (!mediaSession) {
              return
            }

            /*
             * RADIO:
             *
             * No position state.
             */
            if (
              isRadioRef.current
            ) {
              return
            }

            if (
              !(
                "setPositionState" in
                mediaSession
              )
            ) {
              return
            }

            const duration =
              nextAudio.duration

            const position =
              nextAudio.currentTime

            const playbackRate =
              nextAudio.playbackRate ||
              1

            if (
              !Number.isFinite(
                duration
              ) ||
              duration <= 0
            ) {
              return
            }

            if (
              !Number.isFinite(
                position
              ) ||
              position < 0
            ) {
              return
            }

            const safePosition =
              Math.min(
                Math.max(
                  position,
                  0
                ),
                duration
              )

            try {
              mediaSession.setPositionState({
                duration,
                position:
                  safePosition,
                playbackRate,
              })
            } catch {}
          }

        nextAudio.addEventListener(
          "timeupdate",
          updatePosition
        )

        nextAudio.addEventListener(
          "loadedmetadata",
          updatePosition
        )

        nextAudio.addEventListener(
          "durationchange",
          updatePosition
        )

        nextAudio.addEventListener(
          "ratechange",
          updatePosition
        )

        cleanup =
          () => {
            nextAudio.removeEventListener(
              "timeupdate",
              updatePosition
            )

            nextAudio.removeEventListener(
              "loadedmetadata",
              updatePosition
            )

            nextAudio.removeEventListener(
              "durationchange",
              updatePosition
            )

            nextAudio.removeEventListener(
              "ratechange",
              updatePosition
            )
          }
      }

    attach()

    const interval =
      window.setInterval(
        attach,
        100
      )

    const positionInterval =
      window.setInterval(() => {
        if (
          !isRadioRef.current
        ) {
          const currentAudio =
            audioRef.current

          if (
            currentAudio
          ) {
            const mediaSession =
              mediaSessionRef.current

            if (
              mediaSession &&
              "setPositionState" in
                mediaSession
            ) {
              const duration =
                currentAudio.duration

              const position =
                currentAudio.currentTime

              if (
                Number.isFinite(
                  duration
                ) &&
                duration > 0 &&
                Number.isFinite(
                  position
                )
              ) {
                try {
                  mediaSession.setPositionState({
                    duration,
                    position:
                      Math.min(
                        Math.max(
                          position,
                          0
                        ),
                        duration
                      ),
                    playbackRate:
                      currentAudio.playbackRate ||
                      1,
                  })
                } catch {}
              }
            }
          }
        }
      }, 500)

    return () => {
      window.clearInterval(
        interval
      )

      window.clearInterval(
        positionInterval
      )

      cleanup?.()
    }
  }, [
    audioRef,
  ])

  /* =====================================================
     ACTION HANDLERS
     
     IMPORTANTE:
     
     Este efecto depende de isRadio.
     
     Así podemos realmente quitar:
     
       previous
       next
       seek
     
     cuando estamos en radio.
  ===================================================== */

  useEffect(() => {
    const mediaSession =
      mediaSessionRef.current

    if (!mediaSession) {
      return
    }

    /* ===================================================
       PLAY
    =================================================== */

    const handleMediaPlay =
  async () => {

    wantsToPlayRef.current = true

    /*
     * ===============================================
     * NATIVE iOS
     * ===============================================
     */

    if (isNativeApp) {
          window.webkit
            ?.messageHandlers
            ?.mixtape
            ?.postMessage({
              action: "resume",
            })
          
          return
        }
      
        /*
         * ===============================================
         * WEB
         * ===============================================
         */
      
        const audio =
          audioRef.current
      
        if (!audio) {
          return
        }
      
        if (isRadioRef.current) {
          const radio =
            activeRadioRef.current
        
          if (radio) {
            try {
              await playRadioStreamRef.current(
                radio
              )
            } catch {}
          }
        
          return
        }
      
        if (!audio.paused) {
          return
        }
      
        try {
          await resumeAudioRef.current()
        } catch {}
      }

    /* ===================================================
       PAUSE
    =================================================== */

    const handleMediaPause =
      () => {
      
        wantsToPlayRef.current =
          false
      
        /*
         * ===============================================
         * NATIVE
         * ===============================================
         */
      
        if (isNativeApp) {
          window.webkit
            ?.messageHandlers
            ?.mixtape
            ?.postMessage({
              action: "pause",
            })
          
          return
        }
      
        /*
         * ===============================================
         * WEB
         * ===============================================
         */
      
        const audio =
          audioRef.current
      
        if (!audio) {
          return
        }
      
        if (isRadioRef.current) {
          stopRadioStreamRef.current()
          return
        }
      
        audio.pause()
      
        setIsMusicLoadingRef.current(
          false
        )
      }

    /* ===================================================
       PREVIOUS
    =================================================== */

    const handlePrevious =
      () => {
        if (isRadioRef.current) {
          return
        }
      
        if (isNativeApp) {
          window.webkit
            ?.messageHandlers
            ?.mixtape
            ?.postMessage({
              action: "previous",
            })
          
          return
        }
      
        previousSongRef.current()
      }

    /* ===================================================
       NEXT
    =================================================== */

    const handleNext =
      () => {
        if (isRadioRef.current) {
          return
        }
      
        if (isNativeApp) {
          window.webkit
            ?.messageHandlers
            ?.mixtape
            ?.postMessage({
              action: "next",
            })
          
          return
        }
      
        nextSongRef.current()
      }

    /* ===================================================
       SEEK TO
    =================================================== */

    const handleSeekTo = (
      details: MediaSessionActionDetails
    ) => {
    
      if (isRadioRef.current) {
        return
      }
    
      const seekTime =
        details.seekTime
    
      if (
        seekTime === undefined ||
        !Number.isFinite(seekTime)
      ) {
        return
      }
    
      /*
       * ===============================================
       * NATIVE
       * ===============================================
       */
    
      if (isNativeApp) {
        window.webkit
          ?.messageHandlers
          ?.mixtape
          ?.postMessage({
            action: "seek",
            time: seekTime,
          })
        
        return
      }
    
      /*
       * ===============================================
       * WEB
       * ===============================================
       */
    
      const audio =
        audioRef.current
    
      if (!audio) {
        return
      }
    
      const duration =
        audio.duration
    
      const nextTime =
        Number.isFinite(duration) &&
        duration > 0
          ? Math.min(
              Math.max(
                seekTime,
                0
              ),
              duration
            )
          : Math.max(
              seekTime,
              0
            )
          
      try {
        audio.currentTime =
          nextTime
      } catch {}
    }

    try {
      /* =================================================
         PLAY / PAUSE
         
         SIEMPRE DISPONIBLES
      ================================================= */

      mediaSession.setActionHandler(
        "play",
        handleMediaPlay
      )

      mediaSession.setActionHandler(
        "pause",
        handleMediaPause
      )

      /* =================================================
         MÚSICA
         
         CONTROLES DISPONIBLES
      ================================================= */

      if (
        !isRadio
      ) {
        mediaSession.setActionHandler(
          "previoustrack",
          handlePrevious
        )

        mediaSession.setActionHandler(
          "nexttrack",
          handleNext
        )

        mediaSession.setActionHandler(
          "seekto",
          handleSeekTo
        )
      }

      /* =================================================
         RADIO
         
         BLOQUEAMOS COMPLETAMENTE:
         
         - anterior
         - siguiente
         - seek
         - seek backward
         - seek forward
      ================================================= */

      if (
        isRadio
      ) {
        mediaSession.setActionHandler(
          "previoustrack",
          null
        )

        mediaSession.setActionHandler(
          "nexttrack",
          null
        )

        mediaSession.setActionHandler(
          "seekto",
          null
        )

        mediaSession.setActionHandler(
          "seekbackward",
          null
        )

        mediaSession.setActionHandler(
          "seekforward",
          null
        )
      }

      /*
       * Estos nunca los queremos.
       */

      mediaSession.setActionHandler(
        "seekbackward",
        null
      )

      mediaSession.setActionHandler(
        "seekforward",
        null
      )
    } catch (error) {
      console.warn(
        "[MediaSession] Handler registration error:",
        error
      )
    }

    /* ===================================================
       CLEANUP
    =================================================== */

    return () => {
      try {
        mediaSession.setActionHandler(
          "play",
          null
        )

        mediaSession.setActionHandler(
          "pause",
          null
        )

        mediaSession.setActionHandler(
          "previoustrack",
          null
        )

        mediaSession.setActionHandler(
          "nexttrack",
          null
        )

        mediaSession.setActionHandler(
          "seekto",
          null
        )

        mediaSession.setActionHandler(
          "seekbackward",
          null
        )

        mediaSession.setActionHandler(
          "seekforward",
          null
        )
      } catch {}
    }
  }, [
    isRadio,
    audioRef,
  ])
}